import { search } from "fast-fuzzy";
import { useMemo, useState } from "react";
import { connect } from "react-redux";
import { useParams } from "react-router-dom";
import { ArrowIcon, NotInLocationPicture } from "../../Assets/assets";
import {
  NoResultsFallback,
  ZeroStateFallback
} from "../../Components/FallBacks/FallBacks";
import Header from "../../Components/Header/Header";
import SearchBox from "../../Components/SearchBox/SearchBox";
import { setSelectedProcedure } from "../../Redux/procedures/action";
import { isValidArray } from "../../Services/validators";
import {
  dateAndTimeConverter,
  getAppsUrls,
  locationRange,
  openInNewTab
} from "../../Utils/constants";

function Procedures(props) {
  const [searchKey, setSearchKey] = useState("");
  const { locationId } = useParams();

  const results = useMemo(() => {
    const linkedProcedures =
      props.profile.data?.linkedProcedures?.map((data) => ({
        ...data,
        procedure: props.procedures.data?.[data.procedureId],
        location: props.locations.data?.[locationId]
      })) || [];

    const filteredProcedures = props.claims?.moduleAccess?.independentEngineer
      ? linkedProcedures.filter(
          (item) => item.procedure?.procedureType === "independentEngineer"
        )
      : linkedProcedures;

    if (isValidArray(filteredProcedures))
      if (!searchKey) {
        return filteredProcedures;
      }
    return search(searchKey, filteredProcedures, {
      keySelector: (item) => `${item.procedure.title}`,
      threshold: 0.7
    });
  }, [
    props.claims,
    locationId,
    props.locations.data,
    props.procedures.data,
    props.profile.data?.linkedProcedures,
    searchKey
  ]);

  return (
    <div className="inherit-parent-height inherit-parent-width">
      <Header
        theme={props.auth.theme.primaryColor === "#0E0E0E" ? "dark" : "light"}
        title="Procedures"
        backOnClick={() => {
          props.setSelectedProcedure(null);
          props.navigate("/locationSearch");
        }}
        profileOnClick={() => {
          props.setSelectedProcedure(null);
          props.navigate("/settings");
        }}
      />
      <div className="inherit-parent-width remaining-body-height_-128px flex-justify-content-center">
        <div className="inherit-parent-height inherit-parent-width max-width-767px overflow-x-scroll hide-scrollbar">
          {props.locations?.distance !== null &&
            props.locations?.distance < locationRange &&
            props.locations.data &&
            props.procedures.data && (
              <>
                <div className="font-color-tertiaryColor5 text-align-center padding-large">
                  SELECT A PROCEDURE TO EXECUTE
                </div>
                <SearchBox
                  searchKey={searchKey}
                  setSearchKey={setSearchKey}
                  data-cy={
                    searchKey?.length > 0
                      ? "search-clear-button"
                      : "search-button"
                  }
                  placeholder="Search"
                />
              </>
            )}

          {!props.locations.loading &&
            !props.procedures.loading &&
            isValidArray(results) &&
            props.locations?.distance !== null &&
            props.locations?.distance < locationRange &&
            props.locations.data &&
            props.procedures.data && (
              <div
                className="inherit-parent-width padding-left-large padding-right-large"
                data-cy="PROCEDURES-LIST"
              >
                {results.map((data, index) => (
                  <div key={index}>
                    <Card
                      index={index}
                      data={data}
                      onClick={() => {
                        props.setSelectedProcedure(data);
                        props.navigate(`/procedures/${data.procedureId}`);
                      }}
                    />
                  </div>
                ))}
              </div>
            )}

          {!props.locations.loading &&
            !props.procedures.loading &&
            !searchKey &&
            !isValidArray(results) &&
            props.locations?.distance !== null &&
            props.locations?.distance < locationRange && (
              <div
                style={{
                  height: "calc(100% - 120px)"
                }}
              >
                <ZeroStateFallback
                  title="No upcoming procedures"
                  description="There are no scheduled procedures at the selected PCT location."
                  type="PROCEDURE"
                />
              </div>
            )}

          {!props.locations.loading &&
            !props.procedures.loading &&
            searchKey &&
            !isValidArray(results) &&
            props.locations?.distance !== null &&
            props.locations?.distance < locationRange && (
              <NoResultsFallback
                type="PROCEDURE"
                resetOnClick={() => {
                  setSearchKey("");
                }}
              />
            )}

          {(!props.locations?.distance ||
            props.locations?.distance > locationRange) && (
            <div
              className="inherit-parent-height padding-horizontal-large display-flex flex-direction-column flex-justify-content-center flex-align-items-center"
              data-cy="notInLocation-fallBack"
            >
              <NotInLocationPicture />
              <div className="padding-top-larger font-size-default">
                Not in location ?
              </div>
              <div className="padding-top-medium text-align-center font-size-medium">
                Reach the assigned location to continue the procedure.
              </div>
              <div
                onClick={() => {
                  openInNewTab(`${getAppsUrls.public.url}/${locationId}`);
                }}
                className="padding-top-medium font-color-primary text-underline"
              >
                Can't reach ?
              </div>
            </div>
          )}
          {(props.locations.loading || props.procedures.loading) &&
            props.locations.distance !== null &&
            props.locations.distance <= locationRange && (
              <div className="display-flex flex-direction-column inherit-parent-width">
                <Suspense />
              </div>
            )}
        </div>
      </div>
    </div>
  );
}

const Card = (props) => {
  return (
    <div
      style={{
        paddingTop: props.index === 0 ? "0px" : "16px"
      }}
      className="display-flex flex-justify-content-space-between flex-align-items-center font-size-small"
      data-cy={`${props.data.procedureId}-card`}
      key={props.data.documentId}
      onClick={() => {
        props.onClick();
      }}
    >
      {props.data?.procedure?.title && props.data?.location?.location ? (
        <>
          <div>
            <div className="font-size-medium text-capitalize">
              {props.data?.procedure?.title || "N/A"}
            </div>
            <div className="padding-top-small">{`${props.data?.location?.PCTNo?.toString().padStart(
              2,
              "0"
            )} - ${props.data?.location?.location}`}</div>
          </div>
          <div className="display-flex">
            <div className="padding-right-default">
              {dateAndTimeConverter(props.data?.timeStamp, "cardDate")}
            </div>
            <ArrowIcon color="var(--tertiaryColor6)" />
          </div>
        </>
      ) : (
        <>
          <div className="inherit-parent-width">
            <div className="shimmer padding-larger inherit-parent-width" />
          </div>
        </>
      )}
    </div>
  );
};

const mapStateToProps = (state) => ({
  auth: state.auth,
  profile: state.profile,
  procedures: state.procedures,
  locations: state.locations
});

const mapDispatchToProps = function () {
  return {
    setSelectedProcedure: (currentProcedure) =>
      setSelectedProcedure(currentProcedure)
  };
};

export default connect(mapStateToProps, mapDispatchToProps)(Procedures);

const Suspense = () => {
  return [...Array(10)].map((_data, index) => (
    <div className="padding-default" key={index}>
      <div className="shimmer padding-larger inherit-parent-width" />
    </div>
  ));
};
